import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", company: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("La contraseña debe tener al menos 6 caracteres"); return; }
    setLoading(true);
    try {
      await register(form);
      navigate("/portal");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4">
            <img src="https://customer-assets.emergentagent.com/job_promo-quote/artifacts/wgwlu5pd_WhatsApp%20Image%202026-03-27%20at%2018.43.52.jpeg" alt="PYM" className="h-10 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Chivo' }}>Crear Cuenta</h1>
          <p className="text-sm text-[#475569] mt-1">Regístrate para cotizar y dar seguimiento</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm flex items-start gap-2" data-testid="register-error">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Nombre completo *</Label>
              <Input value={form.name} onChange={update("name")} required placeholder="Tu nombre" className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="register-name-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Email *</Label>
              <Input type="email" value={form.email} onChange={update("email")} required placeholder="tu@email.com" className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="register-email-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Contraseña *</Label>
              <Input type="password" value={form.password} onChange={update("password")} required placeholder="Mínimo 6 caracteres" className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="register-password-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Teléfono</Label>
              <Input value={form.phone} onChange={update("phone")} placeholder="+502 1234-5678" className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="register-phone-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Empresa</Label>
              <Input value={form.company} onChange={update("company")} placeholder="Nombre de tu empresa" className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="register-company-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#E30613] text-white font-bold rounded-sm py-2.5 hover:bg-[#C80410] transition-colors" data-testid="register-submit-btn">
              {loading ? "Registrando..." : "Crear Cuenta"}
            </Button>
          </form>
        </div>
        <p className="text-center text-sm text-[#475569] mt-4">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" className="text-[#0047AB] font-medium hover:underline" data-testid="register-login-link">Inicia sesión</Link>
        </p>
      </div>
    </div>
  );
}
